import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  MessageCircle, 
  X, 
  Send, 
  Paperclip, 
  Loader2,
  ChevronDown,
  User,
  Bot,
  Clock,
  HelpCircle,
  Book,
  Search,
  History,
  Image,
  XCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { getBackendUrl } from '../../utils/apiUrl';

const SupportChatWidget = ({
  subdomain,
  storeId,
  storeName = null,
  customerEmail = null,
  customerName = null,
  position = 'bottom-right',
  // v4.24 — the tenant's assistant name (e.g. "Dave"). When set on an
  // openChat storefront widget, the launcher + header read "Ask {name}"
  // so the bubble matches the brand's assistant instead of a generic icon.
  agentName = null,
  initialOpen = false,  // v4.14 — mount already-open (login "Chat with our team")
  // v4.15 — "openChat": anonymous visitors chat IMMEDIATELY (AI answers
  // store questions from public data — no name/email form gate). Contact
  // details (email + phone, both required) are collected only at the
  // talk-to-human handoff, which files the ticket with the transcript and
  // hands over to the normal ticket chat below.
  mode = 'ticket'
}) => {
  // Simple markdown renderer for chat messages
  const renderMarkdown = (content) => {
    if (!content) return null;
    let html = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br />');
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const [isOpen, setIsOpen] = useState(initialOpen);
  const [activeTab, setActiveTab] = useState('chat');
  const [supportInfo, setSupportInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storeInfo, setStoreInfo] = useState(null);
  
  // Chat state
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  
  // Image upload state
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [pendingImage, setPendingImage] = useState(null);
  const fileInputRef = useRef(null);
  
  // Customer info form
  const [showEmailForm, setShowEmailForm] = useState(true);
  const [formEmail, setFormEmail] = useState(customerEmail || '');
  const [formName, setFormName] = useState(customerName || '');
  
  // Previous tickets for this customer
  const [previousTickets, setPreviousTickets] = useState([]);
  const [showPreviousTickets, setShowPreviousTickets] = useState(false);
  
  // FAQ state
  const [faqs, setFaqs] = useState([]);
  const [faqCategories, setFaqCategories] = useState([]);
  const [selectedFaqCategory, setSelectedFaqCategory] = useState(null);
  const [faqSearch, setFaqSearch] = useState('');
  
  // KB state
  const [kbArticles, setKbArticles] = useState([]);
  const [kbCategories, setKbCategories] = useState([]);
  const [kbSearch, setKbSearch] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
const backendUrl = getBackendUrl();
  
  // Generate session key for localStorage
  const getSessionKey = () => {
    return `support_chat_${subdomain}_${storeId || 'unified'}_${formEmail || 'anonymous'}`;
  };
  
  // Save session to localStorage
  const saveSession = useCallback((ticketData) => {
    if (ticketData && formEmail) {
      const sessionData = {
        ticket: ticketData,
        email: formEmail,
        name: formName,
        timestamp: Date.now()
      };
      localStorage.setItem(getSessionKey(), JSON.stringify(sessionData));
    }
  }, [formEmail, formName, subdomain, storeId]);
  
  // Load session from localStorage
  const loadSession = useCallback(() => {
    const sessionKey = getSessionKey();
    const savedSession = localStorage.getItem(sessionKey);
    if (savedSession) {
      try {
        const sessionData = JSON.parse(savedSession);
        // Only restore if session is less than 24 hours old
        if (Date.now() - sessionData.timestamp < 24 * 60 * 60 * 1000) {
          return sessionData;
        } else {
          localStorage.removeItem(sessionKey);
        }
      } catch (e) {
        localStorage.removeItem(sessionKey);
      }
    }
    return null;
  }, [subdomain, storeId, formEmail]);
  
  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Load support info
  useEffect(() => {
    const loadSupportInfo = async () => {
      try {
        const url = `${backendUrl}/api/support/public/info/${subdomain}${storeId ? `?store_id=${storeId}` : ''}`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setSupportInfo(data);
          
          if (customerEmail && customerName) {
            setShowEmailForm(false);
            setFormEmail(customerEmail);
            setFormName(customerName);
          }
        }
      } catch (err) {
        console.error('Failed to load support info:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (subdomain) {
      loadSupportInfo();
    }
  }, [subdomain, storeId, backendUrl, customerEmail, customerName]);
  
  // Load previous tickets for customer - MUST be defined before useEffect that uses it
  const loadPreviousTickets = useCallback(async () => {
    if (!formEmail) return;
    
    try {
      let url = `${backendUrl}/api/support/tickets/${subdomain}/customer?customer_email=${encodeURIComponent(formEmail)}`;
      if (storeId) url += `&store_id=${storeId}`;
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setPreviousTickets(data.tickets || []);
      }
    } catch (err) {
      console.error('Failed to load previous tickets:', err);
    }
  }, [backendUrl, subdomain, storeId, formEmail]);
  
  // Load previous tickets when customer info is available
  useEffect(() => {
    if (customerEmail && isOpen) {
      loadPreviousTickets();
    }
  }, [customerEmail, isOpen, loadPreviousTickets]);
  
  // Restore session on component mount / email change
  useEffect(() => {
    if (formEmail && !ticket) {
      const savedSession = loadSession();
      if (savedSession && savedSession.ticket) {
        // Verify ticket still exists and load its messages
        const verifyAndRestoreTicket = async () => {
          try {
            const res = await fetch(`${backendUrl}/api/support/tickets/${savedSession.ticket.id}/messages`);
            if (res.ok) {
              const data = await res.json();
              if (data.ticket && data.ticket.status !== 'closed') {
                setTicket(data.ticket);
                setMessages(data.messages);
                setShowEmailForm(false);
                setFormName(savedSession.name);
              }
            }
          } catch (err) {
            console.error('Failed to restore session:', err);
            localStorage.removeItem(getSessionKey());
          }
        };
        verifyAndRestoreTicket();
      }
    }
  }, [formEmail, backendUrl, loadSession, ticket]);
  
  // Load FAQs
  const loadFaqs = useCallback(async () => {
    try {
      let url = `${backendUrl}/api/support/public/faqs/${subdomain}`;
      const params = new URLSearchParams();
      if (storeId) params.append('store_id', storeId);
      if (selectedFaqCategory) params.append('category', selectedFaqCategory);
      if (faqSearch) params.append('search', faqSearch);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setFaqs(data.faqs);
        setFaqCategories(data.categories);
      }
    } catch (err) {
      console.error('Failed to load FAQs:', err);
    }
  }, [backendUrl, subdomain, storeId, selectedFaqCategory, faqSearch]);
  
  // Load KB articles
  const loadKbArticles = useCallback(async () => {
    try {
      let url = `${backendUrl}/api/support/public/kb/${subdomain}`;
      const params = new URLSearchParams();
      if (storeId) params.append('store_id', storeId);
      if (kbSearch) params.append('search', kbSearch);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setKbArticles(data.articles);
        setKbCategories(data.categories);
      }
    } catch (err) {
      console.error('Failed to load KB articles:', err);
    }
  }, [backendUrl, subdomain, storeId, kbSearch]);
  
  useEffect(() => {
    if (activeTab === 'faqs' && isOpen) {
      loadFaqs();
    }
  }, [activeTab, isOpen, loadFaqs]);
  
  useEffect(() => {
    if (activeTab === 'kb' && isOpen) {
      loadKbArticles();
    }
  }, [activeTab, isOpen, loadKbArticles]);
  
  // WebSocket connection with reconnection logic
  useEffect(() => {
    if (!ticket || !isOpen) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }
    
    const connectWebSocket = () => {
      const wsUrl = backendUrl.replace('http', 'ws').replace('https', 'wss');
      const ws = new WebSocket(`${wsUrl}/api/support/ws/customer/${ticket.id}?customer_name=${encodeURIComponent(formName)}`);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'new_message') {
            // Add message, avoiding duplicates, and sort by created_at
            setMessages(prev => {
              const exists = prev.some(m => m.id === data.message.id);
              if (exists) return prev;
              const updated = [...prev, data.message];
              // Sort by created_at to maintain correct order
              return updated.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            });
          } else if (data.type === 'typing') {
            setIsTyping(data.is_typing && data.sender_name !== formName);
          } else if (data.type === 'ticket_update') {
            setTicket(prev => ({ ...prev, ...data.data }));
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
      
      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
        setWsConnected(false);
      };
      
      ws.onclose = () => {
        console.log('WebSocket closed');
        setWsConnected(false);
        // Attempt to reconnect after 3 seconds
        if (isOpen && ticket) {
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
        }
      };
      
      wsRef.current = ws;
    };
    
    connectWebSocket();
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [ticket?.id, isOpen, backendUrl, formName]);
  
  // Poll for new messages as fallback (every 5 seconds)
  useEffect(() => {
    if (!ticket || !isOpen) return;
    
    const pollMessages = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/support/tickets/${ticket.id}/messages`);
        if (res.ok) {
          const data = await res.json();
          setMessages(prev => {
            // Merge messages, keeping unique by ID, and sort by created_at
            const newMessages = data.messages || [];
            const existingIds = new Set(prev.map(m => m.id));
            const uniqueNew = newMessages.filter(m => !existingIds.has(m.id));
            if (uniqueNew.length > 0) {
              const merged = [...prev, ...uniqueNew];
              return merged.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            }
            return prev;
          });
        }
      } catch (err) {
        console.error('Failed to poll messages:', err);
      }
    };
    
    // Poll every 5 seconds as fallback for WebSocket
    const pollInterval = setInterval(pollMessages, 5000);
    
    return () => clearInterval(pollInterval);
  }, [ticket?.id, isOpen, backendUrl]);
  
  // Start chat / create ticket
  const startChat = async (initialMessage) => {
    setSending(true);
    try {
      const storeParam = storeId ? `store_id=${encodeURIComponent(storeId)}` : '';
      const res = await fetch(`${backendUrl}/api/support/tickets/${subdomain}?${storeParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: formName,
          customer_email: formEmail,
          initial_message: initialMessage,
          source: storeId ? 'store_widget' : 'unified_widget'
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        const newTicket = { id: data.ticket_id, status: data.status };
        setTicket(newTicket);
        setShowEmailForm(false);
        setNewMessage('');
        
        // Load messages
        const msgRes = await fetch(`${backendUrl}/api/support/tickets/${data.ticket_id}/messages`);
        if (msgRes.ok) {
          const msgData = await msgRes.json();
          setMessages(msgData.messages);
          setTicket(msgData.ticket);
          saveSession(msgData.ticket);
        }
      }
    } catch (err) {
      console.error('Failed to start chat:', err);
    } finally {
      setSending(false);
    }
  };
  
  // ── v4.15 — anonymous open chat (mode="openChat") ─────────────────
  // Stable anonymous key (never email-dependent — the visitor has none yet).
  const aiThreadKey = `support_chat_${subdomain}_${storeId || 'unified'}_anonymous_ai_thread`;
  const [aiMessages, setAiMessages] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`support_chat_${subdomain}_${storeId || 'unified'}_anonymous_ai_thread`) || 'null');
      return Array.isArray(saved) ? saved.slice(-30) : [];
    } catch { return []; }
  });
  const [aiInput, setAiInput] = useState('');
  const [aiSending, setAiSending] = useState(false);
  const [assistantName, setAssistantName] = useState(null);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffName, setHandoffName] = useState('');
  const [handoffEmail, setHandoffEmail] = useState('');
  const [handoffPhone, setHandoffPhone] = useState('');
  const clientSessionRef = useRef(null);
  if (clientSessionRef.current === null) {
    try {
      const k = `support_chat_${subdomain}_ai_client`;
      let cs = localStorage.getItem(k);
      if (!cs) {
        cs = `w_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
        localStorage.setItem(k, cs);
      }
      clientSessionRef.current = cs;
    } catch { clientSessionRef.current = 'w_mem'; }
  }
  useEffect(() => {
    if (mode !== 'openChat') return;
    try { localStorage.setItem(aiThreadKey, JSON.stringify(aiMessages.slice(-30))); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiMessages, mode]);

  const sendAiMessage = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || aiSending) return;
    setAiInput('');
    const nextThread = [...aiMessages, { role: 'user', content: trimmed }];
    setAiMessages(nextThread);
    setAiSending(true);
    try {
      const res = await fetch(`${backendUrl}/api/storefront/ask/${subdomain}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmed,
          history: aiMessages.slice(-12),
          store_slug: storeId || null,
          client_session: clientSessionRef.current,
        }),
      });
      if (res.status === 429) {
        setAiMessages((cur) => [...cur, { role: 'assistant', content: "You're sending messages a little fast — give it a minute and try again." }]);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.assistant_name) setAssistantName(data.assistant_name);
      setAiMessages((cur) => [...cur, { role: 'assistant', content: data.reply }]);
      if (data.handoff_requested) setHandoffOpen(true);
    } catch (err) {
      console.error('Open chat failed:', err);
      setAiMessages((cur) => [...cur, { role: 'assistant', content: "Sorry — something went wrong. Try again, or use the button below to reach the team directly." }]);
    } finally {
      setAiSending(false);
    }
  };

  // Handoff → real support ticket carrying the transcript. Email AND phone
  // are both required (founder spec) so the team can actually reach back.
  const submitHandoff = async () => {
    const email = handoffEmail.trim();
    const phone = handoffPhone.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || phone.replace(/\D/g, '').length < 7) return;
    setSending(true);
    try {
      const transcript = aiMessages.slice(-10)
        .map((m) => `${m.role === 'user' ? 'Visitor' : 'Assistant'}: ${m.content}`)
        .join('\n');
      const storeParam = storeId ? `store_id=${encodeURIComponent(storeId)}` : '';
      const res = await fetch(`${backendUrl}/api/support/tickets/${subdomain}?${storeParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: handoffName.trim() || email.split('@')[0],
          customer_email: email,
          customer_phone: phone,
          subject: 'Chat handoff — visitor wants to talk to the team',
          initial_message: `Visitor asked to talk to a human.\n\nRecent chat:\n${transcript}`,
          source: storeId ? 'store_widget' : 'unified_widget',
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setFormName(handoffName.trim() || email.split('@')[0]);
        setFormEmail(email);
        setHandoffOpen(false);
        const msgRes = await fetch(`${backendUrl}/api/support/tickets/${data.ticket_id}/messages`);
        if (msgRes.ok) {
          const msgData = await msgRes.json();
          setMessages(msgData.messages);
          setTicket(msgData.ticket);
          saveSession(msgData.ticket);
        } else {
          setTicket({ id: data.ticket_id, status: data.status });
        }
      }
    } catch (err) {
      console.error('Handoff failed:', err);
    } finally {
      setSending(false);
    }
  };

  // Resume existing ticket
  const resumeTicket = async (ticketId) => {
    try {
      const res = await fetch(`${backendUrl}/api/support/tickets/${ticketId}/messages`);
      if (res.ok) {
        const data = await res.json();
        setTicket(data.ticket);
        setMessages(data.messages);
        setShowEmailForm(false);
        setShowPreviousTickets(false);
        saveSession(data.ticket);
      }
    } catch (err) {
      console.error('Failed to resume ticket:', err);
    }
  };
  
  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image (JPEG, PNG, GIF, or WebP)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image too large. Maximum size is 5MB.');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setPendingImage(file);
    };
    reader.readAsDataURL(file);
  };
  
  // Clear pending image
  const clearPendingImage = () => {
    setImagePreview(null);
    setPendingImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Upload image to server
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const res = await fetch(`${backendUrl}/api/support/upload-image`, {
      method: 'POST',
      body: formData
    });
    
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Upload failed');
    }
    
    const data = await res.json();
    return data.url;
  };
  
  // Send message (with optional image)
  const sendMessage = async () => {
    if ((!newMessage.trim() && !pendingImage) || !ticket) return;
    
    setSending(true);
    const messageContent = newMessage;
    const imageFile = pendingImage;
    setNewMessage('');
    clearPendingImage();
    
    // Determine message type
    const msgType = imageFile ? 'image' : 'text';
    
    try {
      // Upload image if present
      let imageUrl = null;
      if (imageFile) {
        setUploading(true);
        try {
          imageUrl = await uploadImage(imageFile);
        } catch (uploadErr) {
          console.error('Image upload failed:', uploadErr);
          alert('Failed to upload image. Please try again.');
          setNewMessage(messageContent);
          setSending(false);
          setUploading(false);
          return;
        }
        setUploading(false);
      }
      
      const res = await fetch(`${backendUrl}/api/support/tickets/${ticket.id}/messages?sender_type=customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: messageContent || (imageUrl ? 'Sent an image' : ''),
          message_type: msgType,
          image_url: imageUrl
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Add the sent message (WebSocket will handle duplicates)
        setMessages(prev => {
          if (prev.find(m => m.id === data.message.id)) return prev;
          const updated = [...prev, data.message];
          return updated.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        });
      } else {
        setNewMessage(messageContent);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };
  
  // Handle typing indicator
  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: true }));
      
      setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: false }));
        }
      }, 2000);
    }
  };
  
  // Start new conversation
  const startNewConversation = () => {
    setTicket(null);
    setMessages([]);
    setShowEmailForm(true);
    setNewMessage('');
    localStorage.removeItem(getSessionKey());
  };
  
  // Submit FAQ feedback
  const submitFaqFeedback = async (faqId, helpful) => {
    try {
      await fetch(`${backendUrl}/api/support/public/faq/${faqId}/feedback?helpful=${helpful}`, {
        method: 'POST'
      });
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };
  
  if (loading || !supportInfo) {
    return null;
  }
  
  if (!supportInfo.chat_enabled && !supportInfo.faq_enabled && !supportInfo.knowledge_base_enabled) {
    return null;
  }
  
  const positionClasses = position === 'bottom-left'
    ? 'left-4 sm:left-6'
    : 'right-4 sm:right-6';
  // v5.8 — the storefront "Ask {name}" pill (agentName set) sits LOWER
  // (bottom-6) than the default mobile support bump, so it doesn't float
  // mid-page over the storefront content.
  const launcherBottom = agentName ? 'bottom-6' : 'bottom-20 md:bottom-6';
  
  return (
    <>
      {/* Chat Button — a labeled "Ask {agentName}" pill when the tenant's
          assistant name is provided (storefront), else the plain icon. */}
      {!isOpen && (
        <button
          onClick={() => {
            setIsOpen(true);
            if (formEmail) loadPreviousTickets();
          }}
          className={`fixed ${launcherBottom} ${positionClasses} z-50 shadow-lg flex items-center justify-center gap-2 text-white transition-all hover:scale-105 ${
            agentName ? 'h-12 rounded-full px-4 font-semibold text-sm' : 'w-14 h-14 rounded-full'
          }`}
          style={{ backgroundColor: supportInfo.widget_color }}
          data-testid="support-chat-button"
          aria-label={agentName ? `Ask ${agentName}` : 'Open chat'}
        >
          <MessageCircle className={agentName ? 'h-5 w-5' : 'h-6 w-6'} />
          {agentName && <span>Ask {agentName}</span>}
        </button>
      )}
      
      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed ${launcherBottom} ${positionClasses} z-50 w-[calc(100vw-2rem)] sm:w-96 h-[min(500px,calc(100dvh-7rem))] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200`}
          data-testid="support-chat-window"
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between px-4 py-3 text-white"
            style={{ backgroundColor: supportInfo.widget_color }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold">{agentName ? `Ask ${agentName}` : 'Support'}</h3>
                <p className="text-xs opacity-80">
                  {ticket ? (
                    wsConnected ? 'Connected' : 'Reconnecting...'
                  ) : (
                    supportInfo.is_within_hours
                      ? (supportInfo.agent_available ? 'Online' : 'We\'ll reply soon')
                      : 'Leave a message'
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {ticket && (
                <button 
                  onClick={startNewConversation}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  title="New conversation"
                >
                  <History className="h-4 w-4" />
                </button>
              )}
              <button 
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="close-chat-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200">
            {supportInfo.chat_enabled && (
              <button
                onClick={() => setActiveTab('chat')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'chat'
                    ? 'text-teal-600 border-b-2 border-teal-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="chat-tab"
              >
                <MessageCircle className="h-4 w-4 inline mr-1" />
                Chat
              </button>
            )}
            {supportInfo.faq_enabled && (
              <button
                onClick={() => setActiveTab('faqs')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'faqs'
                    ? 'text-teal-600 border-b-2 border-teal-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="faqs-tab"
              >
                <HelpCircle className="h-4 w-4 inline mr-1" />
                FAQs
              </button>
            )}
            {supportInfo.knowledge_base_enabled && (
              <button
                onClick={() => setActiveTab('kb')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'kb'
                    ? 'text-teal-600 border-b-2 border-teal-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="kb-tab"
              >
                <Book className="h-4 w-4 inline mr-1" />
                Help
              </button>
            )}
          </div>
          
          {/* Content Area */}
          <div className="flex-1 overflow-hidden">
            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <div className="h-full flex flex-col">
                {/* v4.15 — anonymous OPEN chat: ask immediately, no form gate.
                    Once a ticket exists (human handoff) the normal ticket
                    chat below takes over. */}
                {mode === 'openChat' && !ticket && (
                  <div className="h-full flex flex-col" data-testid="open-chat">
                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                      {aiMessages.length === 0 && (
                        <p className="text-sm text-gray-500 p-2">
                          Hi! Ask me anything about {storeName || 'the store'} —
                          plans, passes, pricing, delivery. Want a person instead?
                          Just say “talk to a human”.
                        </p>
                      )}
                      {aiMessages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                            m.role === 'user' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {m.content}
                          </div>
                        </div>
                      ))}
                      {aiSending && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 rounded-2xl px-3 py-2">
                            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                          </div>
                        </div>
                      )}
                      {handoffOpen && (
                        <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-3 space-y-2" data-testid="handoff-form">
                          <p className="text-xs text-gray-600">
                            Share your contact details and the team will take it from here:
                          </p>
                          <Input value={handoffName} onChange={(e) => setHandoffName(e.target.value)}
                            placeholder="Your name (optional)" data-testid="handoff-name" />
                          <Input type="email" value={handoffEmail} onChange={(e) => setHandoffEmail(e.target.value)}
                            placeholder="Email (required)" data-testid="handoff-email" />
                          <Input type="tel" value={handoffPhone} onChange={(e) => setHandoffPhone(e.target.value)}
                            placeholder="Phone number (required)" data-testid="handoff-phone" />
                          <Button
                            onClick={submitHandoff}
                            disabled={sending
                              || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(handoffEmail.trim())
                              || handoffPhone.replace(/\D/g, '').length < 7}
                            className="w-full"
                            data-testid="handoff-submit"
                          >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Connect me with the team'}
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="border-t p-2 flex items-center gap-2">
                      <Input
                        value={aiInput}
                        onChange={(e) => setAiInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAiMessage(aiInput); } }}
                        placeholder={`Ask ${assistantName || 'a question'}…`}
                        data-testid="open-chat-input"
                      />
                      <Button size="sm" onClick={() => sendAiMessage(aiInput)}
                        disabled={aiSending || !aiInput.trim()} data-testid="open-chat-send">
                        Send
                      </Button>
                    </div>
                  </div>
                )}
                {/* Email Form */}
                {mode !== 'openChat' && showEmailForm && supportInfo.require_email && (
                  <div className="flex-1 p-4 overflow-y-auto">
                    <div className="mb-4">
                      <p className="text-gray-600 text-sm mb-4">{supportInfo.widget_greeting}</p>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <Input
                          value={formName}
                          onChange={(e) => setFormName(e.target.value)}
                          placeholder="Your name"
                          data-testid="chat-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <Input
                          type="email"
                          value={formEmail}
                          onChange={(e) => {
                            setFormEmail(e.target.value);
                          }}
                          onBlur={() => {
                            if (formEmail) loadPreviousTickets();
                          }}
                          placeholder="your@email.com"
                          data-testid="chat-email-input"
                        />
                      </div>
                      
                      {/* Previous conversations */}
                      {previousTickets.length > 0 && (
                        <div className="mt-2">
                          <button
                            onClick={() => setShowPreviousTickets(!showPreviousTickets)}
                            className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
                          >
                            <History className="h-4 w-4" />
                            {showPreviousTickets ? 'Hide' : 'Show'} previous conversations ({previousTickets.length})
                          </button>
                          
                          {showPreviousTickets && (
                            <div className="mt-2 space-y-2 max-h-32 overflow-y-auto">
                              {previousTickets.filter(t => t.status !== 'closed').slice(0, 5).map(t => (
                                <button
                                  key={t.id}
                                  onClick={() => resumeTicket(t.id)}
                                  className="w-full p-2 text-left bg-gray-50 hover:bg-gray-100 rounded-lg text-sm"
                                >
                                  <div className="font-medium text-gray-900 truncate">{t.subject || 'Support Chat'}</div>
                                  <div className="text-xs text-gray-500 truncate">{t.last_message_preview}</div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                        <textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="How can we help?"
                          className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                          data-testid="chat-initial-message"
                        />
                      </div>
                      <Button
                        onClick={() => startChat(newMessage)}
                        disabled={!formName || !formEmail || !newMessage.trim() || sending}
                        className="w-full"
                        style={{ backgroundColor: supportInfo.widget_color }}
                        data-testid="start-chat-button"
                      >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Chat'}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Quick start form when customer is known but no ticket */}
                {mode !== 'openChat' && !showEmailForm && !ticket && (
                  <div className="flex-1 p-4 overflow-y-auto">
                    <div className="mb-4">
                      <p className="text-gray-600 text-sm mb-2">{supportInfo.widget_greeting}</p>
                      <p className="text-gray-500 text-xs">Logged in as {formName} ({formEmail})</p>
                    </div>
                    
                    {/* Previous conversations */}
                    {previousTickets.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Continue a conversation:</p>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {previousTickets.filter(t => t.status !== 'closed').slice(0, 3).map(t => (
                            <button
                              key={t.id}
                              onClick={() => resumeTicket(t.id)}
                              className="w-full p-2 text-left bg-gray-50 hover:bg-gray-100 rounded-lg text-sm"
                            >
                              <div className="font-medium text-gray-900 truncate">{t.subject || 'Support Chat'}</div>
                              <div className="text-xs text-gray-500 truncate">{t.last_message_preview}</div>
                            </button>
                          ))}
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-sm font-medium text-gray-700 mb-2">Or start a new conversation:</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                        <textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="How can we help?"
                          className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                          data-testid="chat-quick-message"
                        />
                      </div>
                      <Button
                        onClick={() => startChat(newMessage)}
                        disabled={!newMessage.trim() || sending}
                        className="w-full"
                        style={{ backgroundColor: supportInfo.widget_color }}
                        data-testid="quick-start-chat-button"
                      >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Chat'}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Messages */}
                {!showEmailForm && ticket && (
                  <>
                    <div className="flex-1 p-4 overflow-y-auto space-y-3">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                              msg.sender_type === 'customer'
                                ? `${msg._pending ? 'bg-teal-400' : 'bg-teal-600'} text-white rounded-br-md`
                                : msg.sender_type === 'bot'
                                ? 'bg-purple-100 text-purple-900 rounded-bl-md'
                                : msg.sender_type === 'system'
                                ? 'bg-gray-100 text-gray-600 text-xs text-center w-full rounded-lg'
                                : 'bg-gray-100 text-gray-900 rounded-bl-md'
                            }`}
                          >
                            {msg.sender_type !== 'customer' && msg.sender_type !== 'system' && (
                              <div className="flex items-center gap-1 mb-1">
                                {msg.sender_type === 'bot' ? (
                                  <Bot className="h-3 w-3" />
                                ) : (
                                  <User className="h-3 w-3" />
                                )}
                                <span className="text-xs font-medium">{msg.sender_name}</span>
                              </div>
                            )}
                            <div className="text-sm whitespace-pre-wrap break-words">{renderMarkdown(msg.content)}</div>
                            {msg.image_url && (
                              <img src={msg.image_url} alt="" className="mt-2 rounded-lg max-w-full" />
                            )}
                            {msg._pending && (
                              <span className="text-xs opacity-70">Sending...</span>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {isTyping && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-2">
                            <div className="flex gap-1">
                              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div ref={messagesEndRef} />
                    </div>
                    
                    {/* Message Input */}
                    <div className="p-3 border-t border-gray-200">
                      {/* Image Preview */}
                      {imagePreview && (
                        <div className="mb-2 relative inline-block">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            onClick={clearPendingImage}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                            data-testid="clear-image-button"
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                          {uploading && (
                            <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                              <Loader2 className="h-5 w-5 text-white animate-spin" />
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        {/* Hidden file input */}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageSelect}
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="hidden"
                          data-testid="image-file-input"
                        />
                        
                        {/* Image upload button */}
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={sending || uploading}
                          className="shrink-0"
                          title="Attach image"
                          data-testid="attach-image-button"
                        >
                          <Image className="h-4 w-4 text-gray-500" />
                        </Button>
                        
                        <Input
                          value={newMessage}
                          onChange={(e) => {
                            setNewMessage(e.target.value);
                            handleTyping();
                          }}
                          onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                          placeholder={pendingImage ? "Add a caption..." : "Type a message..."}
                          className="flex-1"
                          data-testid="chat-message-input"
                        />
                        <Button
                          onClick={sendMessage}
                          disabled={(!newMessage.trim() && !pendingImage) || sending || uploading}
                          size="icon"
                          style={{ backgroundColor: supportInfo.widget_color }}
                          data-testid="send-message-button"
                        >
                          {(sending || uploading) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* FAQs Tab */}
            {activeTab === 'faqs' && (
              <div className="h-full flex flex-col">
                <div className="p-3 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      value={faqSearch}
                      onChange={(e) => setFaqSearch(e.target.value)}
                      placeholder="Search FAQs..."
                      className="pl-9"
                      data-testid="faq-search-input"
                    />
                  </div>
                  {faqCategories.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <Badge
                        variant={!selectedFaqCategory ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => setSelectedFaqCategory(null)}
                      >
                        All
                      </Badge>
                      {faqCategories.map(cat => (
                        <Badge
                          key={cat}
                          variant={selectedFaqCategory === cat ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => setSelectedFaqCategory(cat)}
                        >
                          {cat}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {faqs.map((faq) => (
                    <details key={faq.id} className="group bg-gray-50 rounded-lg">
                      <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-900 hover:bg-gray-100 rounded-lg list-none flex justify-between items-center">
                        {faq.question}
                        <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                      </summary>
                      <div className="px-4 pb-3">
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{faq.answer}</p>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => submitFaqFeedback(faq.id, true)}
                            className="text-xs text-gray-500 hover:text-green-600"
                          >
                            👍 Helpful
                          </button>
                          <button
                            onClick={() => submitFaqFeedback(faq.id, false)}
                            className="text-xs text-gray-500 hover:text-red-600"
                          >
                            👎 Not helpful
                          </button>
                        </div>
                      </div>
                    </details>
                  ))}
                  {faqs.length === 0 && (
                    <p className="text-center text-gray-500 text-sm py-8">No FAQs found</p>
                  )}
                </div>
              </div>
            )}
            
            {/* Knowledge Base Tab */}
            {activeTab === 'kb' && (
              <div className="h-full flex flex-col">
                {!selectedArticle ? (
                  <>
                    <div className="p-3 border-b border-gray-200">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          value={kbSearch}
                          onChange={(e) => setKbSearch(e.target.value)}
                          placeholder="Search help articles..."
                          className="pl-9"
                          data-testid="kb-search-input"
                        />
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                      {kbArticles.map((article) => (
                        <button
                          key={article.id}
                          onClick={() => setSelectedArticle(article)}
                          className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <h4 className="font-medium text-gray-900 text-sm">{article.title}</h4>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{article.excerpt}</p>
                          <Badge variant="outline" className="mt-2 text-xs">{article.category}</Badge>
                        </button>
                      ))}
                      {kbArticles.length === 0 && (
                        <p className="text-center text-gray-500 text-sm py-8">No articles found</p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="h-full flex flex-col">
                    <div className="p-3 border-b border-gray-200">
                      <button
                        onClick={() => setSelectedArticle(null)}
                        className="text-sm text-teal-600 hover:text-teal-700"
                      >
                        ← Back to articles
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4">
                      <h2 className="text-lg font-semibold text-gray-900 mb-2">{selectedArticle.title}</h2>
                      <Badge variant="outline" className="mb-4">{selectedArticle.category}</Badge>
                      <div className="prose prose-sm text-gray-600 whitespace-pre-wrap">
                        {selectedArticle.content}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChatWidget;
